import { useQuery } from "@tanstack/react-query";
import { pageParams, problemOf, scholarshipsApi } from "../../../api/scholarships";
import { SCHOLARSHIPS_KEY } from "../model";
import { formatMoney } from "../funding";

// Shared by the student's scholarship screens (journey steps 5 to 7)

export const STUDENT_KEY = [...SCHOLARSHIPS_KEY, "student"];
export const PROFILE_KEY = [...STUDENT_KEY, "profile"];
export const myApplicationKey = (id) => [...STUDENT_KEY, "application", id];

export const STUDENT_STATUSES = {
  DRAFT: { label: "Draft", color: "gray", hint: "Not sent yet. Finish it and submit before the deadline." },
  SUBMITTED: { label: "Submitted", color: "blue", hint: "Received. HEC evaluates applications after the deadline." },
  UNDER_EVALUATION: { label: "Under evaluation", color: "yellow", hint: "HEC's evaluators are marking the applications." },
  RESERVE_LIST: { label: "Reserve list", color: "brand", hint: "You are on the reserve list. You may be put forward if a place becomes free." },
  NOT_SELECTED: { label: "Not selected", color: "red", hint: "Your application was not selected this time." },
  WITHDRAWN: { label: "Withdrawn", color: "gray", hint: "You withdrew this application." },
  CANCELLED: { label: "Cancelled", color: "gray", hint: "You cancelled this application before sending it." },
  NOT_SUBMITTED: { label: "Not submitted", color: "gray", hint: "The deadline passed before this application was submitted." },
  WITH_DG: { label: "With the DG", color: "yellow", hint: "HEC put you forward. The Director General is reviewing it." },
  // Sponsored scholarships: granted once a university admits the student; until then with the sponsor
  WITH_SPONSOR: { label: "With the partner", color: "yellow", hint: "The DG signed off your nomination. The partner decides on the scholarship and your admission to a university." },
  GRANTED: { label: "Granted", color: "green", hint: "Congratulations, a university admitted you and the partner granted you this scholarship." },
  NOT_GRANTED: { label: "Not granted", color: "red", hint: "The partner did not grant you this scholarship." },
  // Loans: the DG, then the Minister; then the student's answer, travel (abroad) and BRD's payments
  RETURNED_TO_HEC: { label: "Returned to HEC", color: "yellow", hint: "The DG sent your loan back to HEC for a check. It is still being considered." },
  WITH_MINISTER: { label: "With the Minister", color: "yellow", hint: "The DG approved your loan. The Minister makes the final decision." },
  APPROVED: { label: "Approved", color: "green", hint: "Congratulations, your loan is approved. Download the approval letter and accept or decline the loan." },
  REJECTED: { label: "Rejected", color: "red", hint: "Your loan was not approved." },
  LOAN_ACCEPTED: { label: "Loan accepted", color: "green", hint: "You accepted the loan. BRD pays it out; send your travel details so that BRD can arrange your ticket." },
  LOAN_DECLINED: { label: "Loan declined", color: "gray", hint: "You declined the loan." },
  TRAVEL_DETAILS_SENT: { label: "Travel details sent", color: "green", hint: "BRD has your travel details and will arrange your ticket." },
  TICKET_ISSUED: { label: "Ticket ready", color: "green", hint: "Your ticket is ready. Have a safe journey." },
  // BRD may pay the tuition in parts: without the amounts, only that a payment was made
  TUITION_PAID: { label: "Tuition payment made", color: "green", hint: "BRD made a tuition payment to the university." },
  ALLOWANCE_SENT: { label: "Allowance sent", color: "green", hint: "BRD sent your allowance." },
};
/**
 * The student's status as shown to them. `tuition` ({ paid, due, currency }), when the loan's
 * amounts are known, tells a tuition paid in full from one BRD has paid only part of.
 */
export const studentStatusOf = (value, tuition) => {
  const status = STUDENT_STATUSES[value] ?? { label: value ?? "—", color: "gray", hint: "" };
  if (value !== "TUITION_PAID" || !tuition?.due) return status;
  if (tuition.paid >= tuition.due) return { ...status, label: "Tuition paid", hint: "BRD paid your tuition to the university in full." };
  return {
    ...status,
    label: "Tuition partly paid",
    hint: `BRD has paid ${formatMoney(tuition.paid, tuition.currency)} of your ${formatMoney(tuition.due, tuition.currency)} tuition to the university. The rest follows.`,
  };
};

// The student's academic profile, or null before they fill it in
export const useAcademicProfile = () =>
  useQuery({
    queryKey: PROFILE_KEY,
    queryFn: async () => {
      try {
        return (await scholarshipsApi.get("student/academic-profile")).data;
      } catch (error) {
        if (problemOf(error)?.status === 404) return null;
        throw error;
      }
    },
  });

// Every open scholarship, each with who may apply and whether the student's profile does
export const useOpenScholarships = () =>
  useQuery({
    queryKey: [...STUDENT_KEY, "scholarships"],
    queryFn: async () => (await scholarshipsApi.get("student/scholarships", { params: pageParams({ size: 100 }) })).data,
  });

export const useStudentScholarship = (id) =>
  useQuery({
    queryKey: [...STUDENT_KEY, "scholarship", id],
    enabled: Boolean(id),
    retry: (count, error) => problemOf(error)?.status !== 404 && count < 2,
    queryFn: async () => (await scholarshipsApi.get(`student/scholarships/${id}`)).data,
  });

export const useMyApplications = () =>
  useQuery({
    queryKey: [...STUDENT_KEY, "applications"],
    queryFn: async () => (await scholarshipsApi.get("student/applications", { params: pageParams({ size: 100 }) })).data?.content ?? [],
  });

export const useMyApplication = (id) =>
  useQuery({
    queryKey: myApplicationKey(id),
    enabled: Boolean(id),
    queryFn: () => scholarshipsApi.get(`student/applications/${id}`),
  });

export const useConsentStatement = () =>
  useQuery({
    queryKey: [...STUDENT_KEY, "consent"],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => (await scholarshipsApi.get("student/consent-statement")).data,
  });

// Days left until a deadline, for "Closes in 3 days"
export const closesIn = (iso) => {
  const days = Math.ceil((new Date(iso) - new Date()) / (24 * 3600 * 1000));
  if (days < 0) return "Closed";
  if (days === 0) return "Closes today";
  return `Closes in ${days} day${days === 1 ? "" : "s"}`;
};
